import { useEffect, useState } from 'react';
import { allowedOrigins } from '../consts/allowedOrigins.consts';

const WIDGET_READY_MESSAGE_TYPE = 'WIDGET_READY';
const WIDGET_INIT_ACK_MESSAGE_TYPE = 'WIDGET_INIT_ACK';
const HANDSHAKE_RETRY_INTERVAL_MS = 180;
const HANDSHAKE_TIMEOUT_MS = 700;
const HANDSHAKE_DEBUG_ENABLED =
  import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging';

const logHandshake = (message: string, details?: Record<string, unknown>) => {
  if (!HANDSHAKE_DEBUG_ENABLED) return;

  console.info(`[widget-handshake] ${message}`, details ?? '');
};

export type WidgetHandshakeStatus = 'pending' | 'authorized' | 'denied';

interface UseWidgetHandshakeParams {
  enabled: boolean;
  expectedParentOrigin?: string;
}

interface WidgetInitAckMessage {
  type: typeof WIDGET_INIT_ACK_MESSAGE_TYPE;
  nonce: string;
}

const isWidgetInitAckMessage = (data: unknown): data is WidgetInitAckMessage => {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return false;
  }

  const message = data as Record<string, unknown>;

  return (
    message.type === WIDGET_INIT_ACK_MESSAGE_TYPE &&
    typeof message.nonce === 'string' &&
    message.nonce.length > 0
  );
};

export const useWidgetHandshake = ({
  enabled,
  expectedParentOrigin,
}: UseWidgetHandshakeParams): WidgetHandshakeStatus => {
  const canStartHandshake = enabled && Boolean(expectedParentOrigin) && window.parent !== window;
  const [status, setStatus] = useState<WidgetHandshakeStatus>(
    canStartHandshake ? 'pending' : 'denied'
  );

  useEffect(() => {
    if (!enabled || !expectedParentOrigin || window.parent === window) {
      setStatus('denied');
      return;
    }

    setStatus('pending');

    let nonce = '';
    let isSettled = false;
    let retryIntervalId: number | undefined;
    let timeoutId: number | undefined;
    let handleMessage: ((event: MessageEvent) => void) | undefined;
    let readyAttempt = 0;
    const startedAt = window.performance.now();

    const cleanup = () => {
      if (retryIntervalId !== undefined) {
        window.clearInterval(retryIntervalId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (handleMessage) {
        window.removeEventListener('message', handleMessage);
      }
    };

    const settle = (nextStatus: Exclude<WidgetHandshakeStatus, 'pending'>) => {
      if (isSettled) return;

      isSettled = true;
      cleanup();
      setStatus(nextStatus);
    };

    handleMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) {
        logHandshake('message ignored: unexpected source');
        return;
      }
      if (event.origin !== expectedParentOrigin) {
        logHandshake('message ignored: unexpected origin', {
          actualOrigin: event.origin,
          expectedOrigin: expectedParentOrigin,
        });
        return;
      }
      if (!allowedOrigins.includes(event.origin)) {
        logHandshake('message ignored: origin is not allowed', { origin: event.origin });
        return;
      }
      if (!isWidgetInitAckMessage(event.data)) {
        logHandshake('message ignored: invalid ACK structure');
        return;
      }
      if (event.data.nonce !== nonce) {
        logHandshake('message ignored: nonce mismatch');
        return;
      }

      logHandshake('WIDGET_INIT_ACK accepted', {
        origin: event.origin,
        elapsedMs: Math.round(window.performance.now() - startedAt),
        noncePrefix: nonce.slice(0, 8),
      });
      settle('authorized');
    };

    window.addEventListener('message', handleMessage);

    try {
      nonce = window.crypto.randomUUID();
    } catch {
      logHandshake('crypto.randomUUID is unavailable');
      settle('denied');
      return;
    }

    logHandshake('started', {
      expectedParentOrigin,
      noncePrefix: nonce.slice(0, 8),
    });

    const sendReady = () => {
      readyAttempt += 1;
      logHandshake('WIDGET_READY sent', {
        attempt: readyAttempt,
        targetOrigin: expectedParentOrigin,
        noncePrefix: nonce.slice(0, 8),
      });
      window.parent.postMessage(
        {
          type: WIDGET_READY_MESSAGE_TYPE,
          nonce,
        },
        expectedParentOrigin
      );
    };

    sendReady();
    retryIntervalId = window.setInterval(sendReady, HANDSHAKE_RETRY_INTERVAL_MS);
    timeoutId = window.setTimeout(() => {
      logHandshake('timeout', {
        elapsedMs: Math.round(window.performance.now() - startedAt),
        attempts: readyAttempt,
      });
      settle('denied');
    }, HANDSHAKE_TIMEOUT_MS);

    return cleanup;
  }, [enabled, expectedParentOrigin]);

  return status;
};
